import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import { epochMsOf, systemClock } from '@sakhtar/calendar';
import {
  V2Card,
  V2CardHeader,
  V2Button,
  V2FormGrid,
  V2FormField,
  LoadingState,
  Select,
} from '@sakhtar/design-system/react';
import { CashflowChart } from '../../lib/charts/index.js';
import { useApiGet } from '../../lib/useApiGet.js';
import { apiPost, ApiError } from '../../lib/apiClient.js';
import { bannerMessageFrom } from '../../lib/formErrors.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { fetchNextFinanceCode } from '../../lib/suggestedCode.js';
import { useCapabilities } from '../../lib/capabilities.js';
import { useTenantSettings, useAmountDisplay } from '../../lib/tenantSettings.js';
import { JalaliDateInput } from '../../lib/date/JalaliDateInput.js';
import { displayJalaliMonth } from '../../lib/date/displayJalali.js';
import type {
  AccountListResponse,
  FiscalPeriodListResponse,
  OpenItemListResponse,
  TreasuryAccountListResponse,
  TreasuryTransactionListResponse,
  BankAccountListResponse,
  TreasurySettlementMethod,
} from '../../lib/types/finance.js';
import { SETTLEMENT_METHOD_LABEL } from '../../lib/types/finance.js';
import type { MasterDataListResponse } from '../../lib/types/masterData.js';
import { formatRialFa, parseRialInput } from '../../lib/finance-display.js';
import { TreasuryOverview } from './TreasuryOverview.js';

const TREASURY_ACCOUNT_KIND_OPTIONS = [
  { value: 'cash', label: 'صندوق' },
  { value: 'bank', label: 'بانک' },
] as const;

const TREASURY_TXN_KIND_OPTIONS = [
  { value: 'receipt', label: 'دریافت' },
  { value: 'payment', label: 'پرداخت' },
] as const;

/**
 * UI_REFERENCE_SOURCE:
 * section: Sakhtar Cloud ERP v2.dc.html — /t/kimia/finance تب «خزانه» (frame
 *   finTreasury) + Sakhtar Finance.dc.html — Treasury cash/bank + receipt/payment
 * canonical pattern: overview (accounts card + «گردش اخیر خزانه» V2DataGrid)
 *   rendered by TreasuryOverview.tsx (see its own UI_REFERENCE_SOURCE for the
 *   PARTIAL_REAL / KIT_CONFORMANT omissions: no balance, no transfer, no bank
 *   reconciliation, no txn description in the API), then the cashflow chart,
 *   then the create forms (bank account · treasury account · receipt/payment).
 * archetype: ENTITY_LIST
 * status: KIT_CONFORMANT — GET .../finance/treasury/accounts and
 *   .../transactions. No client-side settlement math; journal/status from API.
 *   The cashflow chart (ChartKit CashflowChart) sums real *posted*
 *   receipt/payment amountRial rows by month (BigInt-accumulated per
 *   bucket, per FE-P0 FE-HYGIENE-1 — Number() applied once at the
 *   chart-datapoint boundary only), Jalali month labels via
 *   displayJalaliMonth — not a forecast; draft/reversed transactions are
 *   excluded.
 *
 * Create-account (`POST .../treasury/accounts`) and create-receipt/payment
 * (`POST .../treasury/transactions`) are real writes against
 * apps/api/src/finance/treasury.controller.ts.
 *
 * UI_REFERENCE_SOURCE (settlement allocation, added 2026-09-18):
 * section: Sakhtar Finance.dc.html §06 "AR · RECEIPT + SETTLEMENT
 *   ALLOCATION" — party → treasury account → amount → date → a
 *   قلم/مانده/تخصیص table per open item, with a running "باقیمانده"
 *   and a "تخصیص کامل است" state once it reaches zero.
 * archetype: create-form allocation sub-section, real endpoint
 *   (`allocations` on `POST .../treasury/transactions`, ERP-163).
 * status: PARITY — real business rules mirrored, not guessed: the
 *   domain (`executeCreateTransaction`,
 *   packages/modules/accounting/src/subledger/treasury.ts:269-281)
 *   requires the allocation total to *exactly* equal the transaction
 *   amount (no partial cash application), auto-derives both
 *   `counterpartAccountId` and `partyId` from the settled items when
 *   allocations are present (a manually-picked counterpart account is
 *   silently irrelevant once allocating, so the field is hidden, not
 *   just optional), and requires every allocation to share one party.
 *   Per-item cap uses the real `openBalanceRial` as a client-side hint
 *   only — the authoritative check is the server's own reserved-total
 *   guard (`SubledgerOverSettlementError`), same "hint client-side,
 *   authoritative server-side" pattern as MovementForm.tsx.
 * state reference: ready · empty (no open items for this party) ·
 *   permission_denied (403 on ar/ap open-items) · network_error
 *
 * Fixed 2026-09-20 (design-agent re-audit): the allocation total/remaining
 * math and the per-item open-balance display were both re-introducing the
 * exact `Number()`-on-a-Rial-digit-string anti-pattern the cashflow-chart
 * comment above already explains why to avoid — `allocatedTotal`/
 * `remainingToAllocate` now accumulate as `bigint`, and the per-row
 * "مانده (ریال)" cell now goes through `formatRialFa` instead of
 * `Number(...).toLocaleString(...)`.
 *
 * Fixed 2026-09-26 (form-hygiene pass): the "مبلغ (ریال)" transaction-amount
 * input had `inputMode="numeric"` but no digit-only `onChange` filtering,
 * unlike the sibling settlement-allocation input in this same form. Since
 * `remainingToAllocate` unconditionally runs `BigInt(txnAmount.trim())` on
 * every render once a party is selected, a non-digit character typed into
 * this field (e.g. a decimal point) would throw inside `BigInt()` and crash
 * the render, not just fail validation. Fixed with the same
 * `.replace(/\D/g, '')` the allocation input already uses.
 */
export function TreasuryPage() {
  const { unit, label } = useAmountDisplay();
  const { slug } = useParams<{ slug: string }>();
  const { amountDisplayUnit } = useTenantSettings();
  const base = slug ? tenantBasePath(slug) : '';
  const [refreshKey, setRefreshKey] = useState(0);
  const accounts = useApiGet<TreasuryAccountListResponse>(
    slug ? `${base}/api/v1/finance/treasury/accounts` : null,
    [slug, refreshKey],
  );
  const txns = useApiGet<TreasuryTransactionListResponse>(
    slug ? `${base}/api/v1/finance/treasury/transactions` : null,
    [slug, refreshKey],
  );
  const glAccounts = useApiGet<AccountListResponse>(
    slug ? `${base}/api/v1/finance/accounts` : null,
    [slug],
  );
  const fiscalPeriods = useApiGet<FiscalPeriodListResponse>(
    slug ? `${base}/api/v1/accounting/fiscal-periods` : null,
    [slug],
  );
  const parties = useApiGet<MasterDataListResponse>(
    slug ? `${base}/api/v1/master-data/parties?limit=200` : null,
    [slug],
  );
  const bankAccounts = useApiGet<BankAccountListResponse>(
    slug ? `${base}/api/v1/finance/treasury/bank-accounts` : null,
    [slug, refreshKey],
  );

  // --- create bank account (banking identity) ---
  const [bankCode, setBankCode] = useState('');
  const [bankLabel, setBankLabel] = useState('');
  const [bankName, setBankName] = useState('');
  const [bankBranchName, setBankBranchName] = useState('');
  const [bankBranchCode, setBankBranchCode] = useState('');
  const [bankAccountNumber, setBankAccountNumber] = useState('');
  const [bankIban, setBankIban] = useState('');
  const [bankCard, setBankCard] = useState('');
  const [creatingBank, setCreatingBank] = useState(false);
  const [bankError, setBankError] = useState<string | null>(null);

  async function onCreateBank(event: FormEvent) {
    event.preventDefault();
    setBankError(null);
    if (!bankCode.trim() || !bankLabel.trim()) {
      setBankError('کد و عنوان حساب بانکی الزامی است.');
      return;
    }
    if (!bankAccountNumber.trim() && !bankIban.trim()) {
      setBankError('شماره حساب یا شماره شبا را وارد کنید.');
      return;
    }
    setCreatingBank(true);
    try {
      await apiPost(`${base}/api/v1/finance/treasury/bank-accounts`, {
        code: bankCode.trim(),
        name: bankLabel.trim(),
        bankName: bankName.trim() || undefined,
        branchName: bankBranchName.trim() || undefined,
        branchCode: bankBranchCode.trim() || undefined,
        accountNumber: bankAccountNumber.trim() || undefined,
        iban: bankIban.trim() || undefined,
        cardNumber: bankCard.trim() || undefined,
      });
      setBankCode('');
      setBankLabel('');
      setBankName('');
      setBankBranchName('');
      setBankBranchCode('');
      setBankAccountNumber('');
      setBankIban('');
      setBankCard('');
      setRefreshKey((k) => k + 1);
    } catch (error) {
      setBankError(
        bannerMessageFrom(
          error,
          'ثبت حساب بانکی ناموفق بود — شبا، شماره کارت یا کد را بررسی کنید.',
        ),
      );
    } finally {
      setCreatingBank(false);
    }
  }

  const postableGlAccounts = useMemo(
    () =>
      glAccounts.status === 'ready'
        ? [...glAccounts.data.items]
            .filter((a) => a.isPostable && a.status === 'active')
            .sort((a, b) => a.code.localeCompare(b.code))
        : [],
    [glAccounts],
  );

  // --- create treasury account ---
  const [acctCode, setAcctCode] = useState('');
  const [acctName, setAcctName] = useState('');
  const [acctKind, setAcctKind] = useState<'cash' | 'bank'>('cash');
  const [acctGlAccountId, setAcctGlAccountId] = useState('');
  const [acctBankAccountId, setAcctBankAccountId] = useState('');
  const [creatingAccount, setCreatingAccount] = useState(false);
  const [accountError, setAccountError] = useState<string | null>(null);
  const [acctFieldErrors, setAcctFieldErrors] = useState<{ code?: string; name?: string }>({});

  // Codes are never typed from scratch: both forms open with the suggested next code,
  // and refill after each save (refreshKey) so the field is never left empty.
  useEffect(() => {
    let cancelled = false;
    void fetchNextFinanceCode(base, 'bank_account').then((code) => {
      if (!cancelled && code) setBankCode((current) => current || code);
    });
    void fetchNextFinanceCode(base, 'treasury_account').then((code) => {
      if (!cancelled && code) setAcctCode((current) => current || code);
    });
    return () => {
      cancelled = true;
    };
  }, [base, refreshKey]);

  async function onCreateAccount(event: FormEvent) {
    event.preventDefault();
    setAccountError(null);
    const trimmedCode = acctCode.trim();
    const trimmedName = acctName.trim();
    const nextFieldErrors: { code?: string; name?: string } = {};
    if (!trimmedCode) nextFieldErrors.code = 'کد حساب خزانه را وارد کنید.';
    if (!trimmedName) nextFieldErrors.name = 'نام حساب خزانه را وارد کنید.';
    if (Object.keys(nextFieldErrors).length > 0) {
      setAcctFieldErrors(nextFieldErrors);
      return;
    }
    setAcctFieldErrors({});
    if (!acctGlAccountId) {
      setAccountError('حساب کل مرتبط را انتخاب کنید.');
      return;
    }
    if (acctKind === 'bank' && !acctBankAccountId) {
      setAccountError('برای خزانه‌ی بانکی، حساب بانکی را انتخاب یا ابتدا تعریف کنید.');
      return;
    }
    setCreatingAccount(true);
    try {
      await apiPost(`${base}/api/v1/finance/treasury/accounts`, {
        code: trimmedCode,
        name: trimmedName,
        kind: acctKind,
        glAccountId: acctGlAccountId,
        ...(acctKind === 'bank' ? { bankAccountId: acctBankAccountId } : {}),
      });
      setAcctCode('');
      setAcctBankAccountId('');
      setAcctName('');
      setAcctKind('cash');
      setAcctGlAccountId('');
      setRefreshKey((k) => k + 1);
    } catch (error) {
      setAccountError(
        bannerMessageFrom(error, 'ثبت حساب خزانه ناموفق بود — کد تکراری یا اطلاعات نامعتبر است.'),
      );
    } finally {
      setCreatingAccount(false);
    }
  }

  // --- create receipt/payment ---
  const [txnKind, setTxnKind] = useState<'receipt' | 'payment'>('receipt');
  const [txnAccountId, setTxnAccountId] = useState('');
  const [txnSourceRef, setTxnSourceRef] = useState('');
  const [txnDate, setTxnDate] = useState('');
  const [txnFiscalPeriodId, setTxnFiscalPeriodId] = useState('');
  const [txnAmount, setTxnAmount] = useState('');
  const [txnPartyId, setTxnPartyId] = useState('');
  const [txnCounterpartAccountId, setTxnCounterpartAccountId] = useState('');
  const [txnDescription, setTxnDescription] = useState('');
  const [txnMethod, setTxnMethod] = useState<TreasurySettlementMethod | ''>('');
  const [txnBankReference, setTxnBankReference] = useState('');
  const [creatingTxn, setCreatingTxn] = useState(false);
  const selectedTreasuryKind =
    accounts.status === 'ready'
      ? accounts.data.items.find((a) => a.treasuryAccountId === txnAccountId)?.kind
      : undefined;
  // Cash moves only through a cash box; every other method needs a bank account
  // and the bank's tracking number (mirrors assertSettlementMethod server-side).
  const methodOptions: readonly TreasurySettlementMethod[] =
    selectedTreasuryKind === 'cash'
      ? ['cash']
      : selectedTreasuryKind === 'bank'
        ? ['pos', 'bank_transfer', 'card_to_card']
        : [];
  const needsBankReference = txnMethod !== '' && txnMethod !== 'cash';
  const [txnError, setTxnError] = useState<string | null>(null);

  // GAP-PP-01 / RL-QA-05 / FE-F-05 / ACC-N-09:
  // Settle capabilities gate: receipts settle AR, payments settle AP.
  // For a pure treasury role without settle permission, open-items is never fetched
  // and the allocation section is omitted entirely.
  const { canSettleAr, canSettleAp } = useCapabilities(slug);
  const canSettle = txnKind === 'receipt' ? canSettleAr : canSettleAp;

  const [allocations, setAllocations] = useState<Record<string, string>>({});
  const openItemKind: 'ar' | 'ap' = txnKind === 'receipt' ? 'ar' : 'ap';
  const openItems = useApiGet<OpenItemListResponse>(
    slug && txnPartyId && canSettle ? `${base}/api/v1/finance/${openItemKind}/open-items` : null,
    [slug, openItemKind, txnPartyId, canSettle],
  );
  const partyOpenItems = useMemo(() => {
    if (!canSettle || openItems.status !== 'ready' || !txnPartyId) return [];
    return openItems.data.items.filter(
      (item) =>
        item.partyId === txnPartyId && (item.status === 'open' || item.status === 'partial'),
    );
  }, [canSettle, openItems, txnPartyId]);

  function resetAllocationsFor(nextPartyId: string, nextKind: 'receipt' | 'payment') {
    setTxnPartyId(nextPartyId);
    setTxnKind(nextKind);
    setAllocations({});
  }

  // BigInt-accumulated per FE-P0 FE-HYGIENE-1 (same rule already applied to
  // the cashflow chart below) — summing multiple Rial allocation amounts as
  // Number() would silently lose precision on large digit strings; digits
  // are validated on input (onChange strips non-digits), so a bare BigInt()
  // parse is safe here.
  const allocatedTotal = canSettle
    ? Object.values(allocations).reduce((sum, v) => sum + (v.trim() ? BigInt(v.trim()) : 0n), 0n)
    : 0n;
  const remainingToAllocate = (txnAmount.trim() ? BigInt(txnAmount.trim()) : 0n) - allocatedTotal;
  const isAllocating = canSettle && allocatedTotal > 0n;

  async function onCreateTxn(event: FormEvent) {
    event.preventDefault();
    setTxnError(null);
    const period =
      fiscalPeriods.status === 'ready'
        ? fiscalPeriods.data.items.find((p) => p.fiscalPeriodId === txnFiscalPeriodId)
        : undefined;
    if (!txnAccountId || !period || !txnAmount.trim()) {
      setTxnError('حساب خزانه، دورهٔ مالی و مبلغ الزامی است.');
      return;
    }
    if (!txnMethod) {
      setTxnError('روش تسویه را انتخاب کنید.');
      return;
    }
    if (needsBankReference && !txnBankReference.trim()) {
      setTxnError('برای پوز، حواله یا کارت به کارت، شماره پیگیری/مرجع بانکی الزامی است.');
      return;
    }
    if ((!canSettle || !isAllocating) && !txnCounterpartAccountId) {
      setTxnError('بدون تخصیص به اقلام باز، انتخاب حساب معین مقابل الزامی است.');
      return;
    }
    if (canSettle && isAllocating && remainingToAllocate !== 0n) {
      setTxnError('تخصیص باید دقیقاً برابر مبلغ تراکنش باشد — باقیمانده باید صفر شود.');
      return;
    }
    const allocationRows = canSettle
      ? Object.entries(allocations)
          .filter(([, amount]) => amount.trim() !== '' && BigInt(amount.trim()) > 0n)
          .map(([openItemId, amountRial]) => ({
            openItemKind,
            openItemId,
            amountRial: amountRial.trim(),
          }))
      : [];
    setCreatingTxn(true);
    try {
      await apiPost(`${base}/api/v1/finance/treasury/transactions`, {
        kind: txnKind,
        treasuryAccountId: txnAccountId,
        sourceReference: txnSourceRef.trim() || `manual-${epochMsOf(systemClock.now())}`,
        businessDate: txnDate,
        fiscalYearId: period.fiscalYearId,
        fiscalPeriodId: period.fiscalPeriodId,
        amountRial: txnAmount.trim(),
        partyId: txnPartyId || undefined,
        // Auto-derived server-side from the settled items when allocating
        // (executeCreateTransaction) — sending one here would be ignored,
        // so it's only sent on the plain, non-allocated path.
        ...(allocationRows.length > 0
          ? { allocations: allocationRows }
          : { counterpartAccountId: txnCounterpartAccountId }),
        description: txnDescription.trim() || undefined,
        settlementMethod: txnMethod,
        ...(needsBankReference ? { bankReference: txnBankReference.trim() } : {}),
      });
      setTxnAccountId('');
      setTxnMethod('');
      setTxnBankReference('');
      setTxnSourceRef('');
      setTxnDate('');
      setTxnFiscalPeriodId('');
      setTxnAmount('');
      setTxnPartyId('');
      setTxnCounterpartAccountId('');
      setTxnDescription('');
      setAllocations({});
      setRefreshKey((k) => k + 1);
    } catch (error) {
      if (error instanceof ApiError && error.status === 422) {
        setTxnError(error.envelope?.error?.message ?? 'اطلاعات واردشده نامعتبر است.');
      } else {
        setTxnError(
          bannerMessageFrom(
            error,
            txnKind === 'receipt' ? 'ثبت دریافت ناموفق بود.' : 'ثبت پرداخت ناموفق بود.',
          ),
        );
      }
    } finally {
      setCreatingTxn(false);
    }
  }

  if (!slug) return null;

  const cashflowPeriods =
    txns.status === 'ready'
      ? (() => {
          // FE-P0 FE-HYGIENE-1: BigInt-accumulated per month bucket — the
          // prior `+= Number(t.amountRial)` summed real Rial amounts as
          // floats across every transaction in a month, the exact
          // anti-pattern AGENTS.md §8 rules out. Number() applies once
          // per already-aggregated bucket, at the chart-datapoint
          // boundary only.
          const byMonth = new Map<string, { inflowRial: bigint; outflowRial: bigint }>();
          for (const t of txns.data.items) {
            if (t.status !== 'posted') continue;
            const month = t.businessDate.slice(0, 7);
            const row = byMonth.get(month) ?? { inflowRial: 0n, outflowRial: 0n };
            if (t.kind === 'receipt') row.inflowRial += BigInt(t.amountRial);
            else if (t.kind === 'payment') row.outflowRial += BigInt(t.amountRial);
            byMonth.set(month, row);
          }
          return [...byMonth.entries()]
            .sort((a, b) => a[0].localeCompare(b[0]))
            .map(([period, row]) => ({
              period: displayJalaliMonth(period),
              inflowRial: Number(row.inflowRial),
              outflowRial: Number(row.outflowRial),
            }));
        })()
      : [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ fontSize: 'var(--sk-fs-sm)', color: 'var(--sk-fg-muted)' }}>
        هر دریافت یا پرداخت یک سند حسابداری پیش‌نویس می‌سازد؛ پس از تأیید و قطعی‌شدن سند، مانده‌ی
        صندوق/بانک و اقلام باز تسویه‌شده به‌روز می‌شود.
      </div>

      <TreasuryOverview
        base={base}
        slug={slug}
        unit={unit}
        accounts={accounts}
        txns={txns}
        bankAccounts={bankAccounts}
      />

      {txns.status === 'ready' && cashflowPeriods.length > 0 ? (
        <V2Card>
          <V2CardHeader title="گردش نقدی ماهانه" />
          <div style={{ padding: '16px 20px' }}>
            <CashflowChart
              ariaLabel="گردش نقدی ماهانه"
              amountUnit={amountDisplayUnit}
              periods={cashflowPeriods}
            />
          </div>
        </V2Card>
      ) : null}

      <V2Card>
        <V2CardHeader
          title="حساب‌های بانکی"
          subtitle="اطلاعات کامل حساب: بانک، شعبه، شماره حساب، شبا و شماره کارت"
        />
        <form
          noValidate
          onSubmit={onCreateBank}
          style={{ padding: '0 20px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}
        >
          <V2FormGrid>
            <V2FormField label="کد" required width="short">
              <input
                className="sk-input ltr"
                value={bankCode}
                onChange={(e) => setBankCode(e.target.value)}
              />
            </V2FormField>
            <V2FormField label="عنوان حساب" required width="wide">
              <input
                className="sk-input"
                value={bankLabel}
                placeholder="مثلاً جاری ملت مرکزی"
                onChange={(e) => setBankLabel(e.target.value)}
              />
            </V2FormField>
            <V2FormField label="نام بانک" width="medium">
              <input
                className="sk-input"
                value={bankName}
                onChange={(e) => setBankName(e.target.value)}
              />
            </V2FormField>
            <V2FormField label="نام شعبه" width="medium">
              <input
                className="sk-input"
                value={bankBranchName}
                onChange={(e) => setBankBranchName(e.target.value)}
              />
            </V2FormField>
            <V2FormField label="کد شعبه" width="short">
              <input
                className="sk-input ltr"
                inputMode="numeric"
                value={bankBranchCode}
                onChange={(e) => setBankBranchCode(e.target.value)}
              />
            </V2FormField>
            <V2FormField label="شماره حساب" width="medium">
              <input
                className="sk-input ltr"
                value={bankAccountNumber}
                onChange={(e) => setBankAccountNumber(e.target.value)}
              />
            </V2FormField>
            <V2FormField label="شماره شبا" width="wide" hint="IR و ۲۴ رقم — اعتبارسنجی می‌شود">
              <input
                className="sk-input ltr"
                placeholder="IR000000000000000000000000"
                value={bankIban}
                onChange={(e) => setBankIban(e.target.value)}
              />
            </V2FormField>
            <V2FormField label="شماره کارت" width="medium">
              <input
                className="sk-input ltr"
                inputMode="numeric"
                maxLength={19}
                value={bankCard}
                onChange={(e) => setBankCard(e.target.value)}
              />
            </V2FormField>
          </V2FormGrid>
          {bankError ? (
            <div style={{ fontSize: 13, color: 'var(--sk-danger)' }}>{bankError}</div>
          ) : null}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <V2Button type="submit" variant="primary" icon="plus" disabled={creatingBank}>
              {creatingBank ? 'در حال ثبت…' : 'ثبت حساب بانکی'}
            </V2Button>
          </div>
        </form>
        {bankAccounts.status === 'ready' && bankAccounts.data.items.length > 0 ? (
          <div className="sk-table-scroll">
            <table className="sk-table sk-table--compact">
              <thead>
                <tr>
                  <th>کد</th>
                  <th>عنوان</th>
                  <th>بانک / شعبه</th>
                  <th>شماره حساب</th>
                  <th>شبا</th>
                  <th>شماره کارت</th>
                </tr>
              </thead>
              <tbody>
                {bankAccounts.data.items.map((b) => (
                  <tr key={b.bankAccountId}>
                    <td className="ltr sk-num">{b.code}</td>
                    <td>{b.name}</td>
                    <td>
                      {[b.bankName, b.branchName, b.branchCode ? `کد ${b.branchCode}` : null]
                        .filter(Boolean)
                        .join(' · ') || '—'}
                    </td>
                    <td className="ltr sk-num">{b.accountNumber ?? '—'}</td>
                    <td className="ltr sk-num">{b.iban ?? '—'}</td>
                    <td className="ltr sk-num">{b.cardNumber ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </V2Card>

      <V2Card>
        <V2CardHeader title="حساب خزانه جدید" subtitle="افزودن صندوق یا حساب بانکی" />
        <form
          noValidate
          onSubmit={onCreateAccount}
          style={{ padding: '0 20px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}
        >
          <V2FormGrid>
            <V2FormField label="کد" required width="short" error={acctFieldErrors.code}>
              <input
                className="sk-input ltr"
                value={acctCode}
                onChange={(e) => {
                  setAcctCode(e.target.value);
                  if (acctFieldErrors.code)
                    setAcctFieldErrors((f) => {
                      const next = { ...f };
                      delete next.code;
                      return next;
                    });
                }}
              />
            </V2FormField>
            <V2FormField label="نام" required width="wide" error={acctFieldErrors.name}>
              <input
                className="sk-input"
                value={acctName}
                onChange={(e) => {
                  setAcctName(e.target.value);
                  if (acctFieldErrors.name)
                    setAcctFieldErrors((f) => {
                      const next = { ...f };
                      delete next.name;
                      return next;
                    });
                }}
              />
            </V2FormField>
            <V2FormField label="نوع" required width="medium">
              <Select
                className="sk-select"
                value={acctKind}
                onChange={(e) => {
                  setAcctKind(e.target.value as 'cash' | 'bank');
                  setAcctBankAccountId('');
                }}
              >
                {TREASURY_ACCOUNT_KIND_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
            </V2FormField>
            <V2FormField
              label="حساب کل مرتبط"
              required
              width="wide"
              hint="حسابی که این خزانه در دفتر کل نمایندگی می‌کند"
            >
              <Select
                className="sk-select"
                value={acctGlAccountId}
                onChange={(e) => setAcctGlAccountId(e.target.value)}
              >
                <option value="">انتخاب حساب</option>
                {postableGlAccounts.map((a) => (
                  <option key={a.accountId} value={a.accountId}>
                    {a.code} — {a.name}
                  </option>
                ))}
              </Select>
            </V2FormField>
            {acctKind === 'bank' ? (
              <V2FormField
                label="حساب بانکی"
                required
                width="wide"
                hint="از فهرست «حساب‌های بانکی» بالا؛ شبا و شماره حساب همان‌جا ثبت می‌شود"
              >
                <Select
                  className="sk-select"
                  value={acctBankAccountId}
                  onChange={(e) => setAcctBankAccountId(e.target.value)}
                >
                  <option value="">انتخاب حساب بانکی</option>
                  {bankAccounts.status === 'ready' &&
                    bankAccounts.data.items
                      .filter((b) => b.status === 'active')
                      .map((b) => (
                        <option key={b.bankAccountId} value={b.bankAccountId}>
                          {b.code} — {b.name}
                          {b.bankName ? ` · ${b.bankName}` : ''}
                        </option>
                      ))}
                </Select>
              </V2FormField>
            ) : null}
          </V2FormGrid>
          {accountError ? (
            <div style={{ fontSize: 13, color: 'var(--sk-danger)' }}>{accountError}</div>
          ) : null}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <V2Button type="submit" variant="primary" icon="plus" disabled={creatingAccount}>
              {creatingAccount ? 'در حال ثبت…' : 'ایجاد حساب خزانه'}
            </V2Button>
          </div>
        </form>
      </V2Card>

      <V2Card>
        <V2CardHeader title="ثبت دریافت یا پرداخت" />
        <form
          noValidate
          onSubmit={onCreateTxn}
          style={{ padding: '0 20px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}
        >
          <V2FormGrid>
            <V2FormField label="نوع تراکنش" required width="medium">
              <Select
                className="sk-select"
                value={txnKind}
                onChange={(e) =>
                  resetAllocationsFor(txnPartyId, e.target.value as 'receipt' | 'payment')
                }
              >
                {TREASURY_TXN_KIND_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
            </V2FormField>
            <V2FormField label="حساب خزانه" required width="wide">
              <Select
                className="sk-select"
                value={txnAccountId}
                onChange={(e) => {
                  setTxnAccountId(e.target.value);
                  const kind =
                    accounts.status === 'ready'
                      ? accounts.data.items.find((a) => a.treasuryAccountId === e.target.value)
                          ?.kind
                      : undefined;
                  setTxnMethod(kind === 'cash' ? 'cash' : '');
                  setTxnBankReference('');
                }}
              >
                <option value="">انتخاب حساب</option>
                {accounts.status === 'ready' &&
                  accounts.data.items.map((a) => (
                    <option key={a.treasuryAccountId} value={a.treasuryAccountId}>
                      {a.code} — {a.name}
                    </option>
                  ))}
              </Select>
            </V2FormField>
            <V2FormField
              label="روش تسویه"
              required
              width="medium"
              hint={txnAccountId ? undefined : 'ابتدا حساب خزانه را انتخاب کنید'}
            >
              <Select
                className="sk-select"
                value={txnMethod}
                disabled={methodOptions.length === 0}
                onChange={(e) => setTxnMethod(e.target.value as TreasurySettlementMethod | '')}
              >
                <option value="">انتخاب روش</option>
                {methodOptions.map((m) => (
                  <option key={m} value={m}>
                    {SETTLEMENT_METHOD_LABEL[m]}
                  </option>
                ))}
              </Select>
            </V2FormField>
            {needsBankReference ? (
              <V2FormField
                label="شماره پیگیری / مرجع بانکی"
                required
                width="medium"
                hint="شماره پیگیری پوز، شماره حواله یا شماره مرجع کارت به کارت"
              >
                <input
                  className="sk-input ltr"
                  maxLength={64}
                  value={txnBankReference}
                  onChange={(e) => setTxnBankReference(e.target.value)}
                />
              </V2FormField>
            ) : null}
            <V2FormField label="تاریخ" required width="medium">
              <JalaliDateInput value={txnDate} onChange={setTxnDate} />
            </V2FormField>
            <V2FormField label="دورهٔ مالی" required width="medium">
              <Select
                className="sk-select"
                value={txnFiscalPeriodId}
                onChange={(e) => setTxnFiscalPeriodId(e.target.value)}
              >
                <option value="">انتخاب دوره</option>
                {fiscalPeriods.status === 'ready' &&
                  fiscalPeriods.data.items.map((p) => (
                    <option
                      key={p.fiscalPeriodId}
                      value={p.fiscalPeriodId}
                      disabled={p.postingState !== 'open'}
                    >
                      {p.name} {p.postingState !== 'open' ? '· بسته' : ''}
                    </option>
                  ))}
              </Select>
            </V2FormField>
            <V2FormField label="مبلغ (ریال)" required width="short">
              <input
                className="sk-input ltr"
                inputMode="numeric"
                value={txnAmount}
                onChange={(e) => setTxnAmount(parseRialInput(e.target.value))}
              />
            </V2FormField>
            <V2FormField
              label="طرف‌حساب"
              width="wide"
              hint={
                canSettle
                  ? 'اختیاری — انتخاب طرف‌حساب امکان تخصیص به اقلام باز را باز می‌کند'
                  : 'اختیاری'
              }
            >
              <Select
                className="sk-select"
                value={txnPartyId}
                onChange={(e) => resetAllocationsFor(e.target.value, txnKind)}
              >
                <option value="">— بدون طرف‌حساب —</option>
                {parties.status === 'ready' &&
                  parties.data.items.map((p) => (
                    <option key={String(p.partyId)} value={String(p.partyId)}>
                      {String(p.code)} — {String(p.name)}
                    </option>
                  ))}
              </Select>
            </V2FormField>
            {!isAllocating || !canSettle ? (
              <V2FormField
                label="حساب معین مقابل"
                required
                width="wide"
                hint="مثلاً هزینه یا درآمد مرتبط"
              >
                <Select
                  className="sk-select"
                  value={txnCounterpartAccountId}
                  onChange={(e) => setTxnCounterpartAccountId(e.target.value)}
                >
                  <option value="">انتخاب حساب</option>
                  {postableGlAccounts.map((a) => (
                    <option key={a.accountId} value={a.accountId}>
                      {a.code} — {a.name}
                    </option>
                  ))}
                </Select>
              </V2FormField>
            ) : (
              <V2FormField label="حساب معین مقابل" width="wide">
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    height: 38,
                    fontSize: 13,
                    color: 'var(--sk-fg-muted)',
                  }}
                >
                  از تسویهٔ اقلام باز تعیین می‌شود
                </div>
              </V2FormField>
            )}
            <V2FormField label="شرح" width="full">
              <input
                className="sk-input"
                value={txnDescription}
                onChange={(e) => setTxnDescription(e.target.value)}
              />
            </V2FormField>
          </V2FormGrid>

          {txnPartyId && canSettle ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
                padding: 14,
                borderRadius: 'var(--sk-r-md)',
                background: 'var(--sk-bg-canvas)',
                border: '1px solid var(--sk-border)',
              }}
            >
              <span style={{ fontSize: 13, fontWeight: 600 }}>تخصیص به اقلام باز</span>
              {openItems.status === 'loading' ? <LoadingState /> : null}
              {openItems.status === 'error' ? (
                <span style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}>
                  {openItems.error.status === 403
                    ? 'دسترسی به اقلام باز ندارید.'
                    : 'اقلام باز قابل نمایش نیست.'}
                </span>
              ) : null}
              {openItems.status === 'ready' && partyOpenItems.length === 0 ? (
                <span style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}>
                  قلم بازی وجود ندارد — همهٔ {txnKind === 'receipt' ? 'مطالبات' : 'بدهی‌های'} این
                  طرف‌حساب تسویه شده است.
                </span>
              ) : null}
              {openItems.status === 'ready' && partyOpenItems.length > 0 ? (
                <>
                  <div className="sk-table-scroll">
                    <table className="sk-table sk-table--compact">
                      <thead>
                        <tr>
                          <th>قلم</th>
                          <th style={{ width: 140 }}>مانده ({label})</th>
                          <th style={{ width: 140 }}>تخصیص ({label})</th>
                        </tr>
                      </thead>
                      <tbody>
                        {partyOpenItems.map((item) => (
                          <tr key={item.openItemId}>
                            <td
                              className="ltr sk-num"
                              style={{ fontFamily: 'var(--sk-font-mono)' }}
                            >
                              {item.sourceReference}
                            </td>
                            <td className="sk-num">{formatRialFa(item.openBalanceRial, unit)}</td>
                            <td>
                              <input
                                className="sk-input ltr sk-num"
                                style={{ width: '100%' }}
                                inputMode="numeric"
                                value={allocations[item.openItemId] ?? ''}
                                onChange={(e) =>
                                  setAllocations((prev) => ({
                                    ...prev,
                                    [item.openItemId]: parseRialInput(e.target.value),
                                  }))
                                }
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      gap: 8,
                      fontSize: 13,
                    }}
                  >
                    {isAllocating && remainingToAllocate === 0n ? (
                      <span style={{ color: 'var(--sk-success)', fontWeight: 600 }}>
                        تخصیص کامل است
                      </span>
                    ) : (
                      <span
                        className="sk-num"
                        style={{
                          color: isAllocating ? 'var(--sk-danger)' : 'var(--sk-fg-muted)',
                        }}
                      >
                        باقیمانده: {remainingToAllocate.toLocaleString('fa-IR')}
                      </span>
                    )}
                  </div>
                </>
              ) : null}
            </div>
          ) : null}

          {txnError ? (
            <div style={{ fontSize: 13, color: 'var(--sk-danger)' }}>{txnError}</div>
          ) : null}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <V2Button type="submit" variant="primary" icon="plus" disabled={creatingTxn}>
              {creatingTxn ? 'در حال ثبت…' : 'ثبت تراکنش'}
            </V2Button>
          </div>
        </form>
      </V2Card>
    </div>
  );
}
